import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  CargoCategoriesListParams,
  CargoCategory,
  PatchedCargoCategory,
  PatchedVehicleType,
  VehicleType,
  VehicleTypeBulk, VehicleTypesListParams,
} from '@apiTypes'

/** Page size used when hydrating the session-wide vehicle-type catalog. */
export const VEHICLE_TYPES_CATALOG_PAGE_SIZE = 1000

export const useCatalogsStore = defineStore('catalogs', () => {
  const vehicleTypes = ref<VehicleType[]>([])
  const vehicleTypesLoading = ref(false)
  const vehicleTypesLoaded = ref(false)
  const vehicleTypesError = ref(false)

  let vehicleTypesPromise: Promise<VehicleType[]> | undefined
  let vehicleTypesRequestId = 0

  function fetchVehicleTypes(query: VehicleTypesListParams) {
    return api.vehicleTypes.vehicleTypesList(query)
  }

  /**
   * Loads the full vehicle-type catalog once per session (shared by booking form steps).
   * Concurrent callers share the in-flight request; later calls return cached results.
   */
  async function ensureVehicleTypes(): Promise<VehicleType[]> {
    if (vehicleTypesLoaded.value)
      return vehicleTypes.value

    if (vehicleTypesPromise)
      return vehicleTypesPromise

    const thisRequest = ++vehicleTypesRequestId

    vehicleTypesLoading.value = true
    vehicleTypesError.value = false

    vehicleTypesPromise = fetchVehicleTypes({ pageSize: VEHICLE_TYPES_CATALOG_PAGE_SIZE })
      .then((response) => {
        if (thisRequest !== vehicleTypesRequestId)
          return vehicleTypes.value

        vehicleTypes.value = response.data.results ?? []
        vehicleTypesLoaded.value = true
        vehicleTypesError.value = false

        return vehicleTypes.value
      })
      .catch(() => {
        if (thisRequest !== vehicleTypesRequestId)
          return vehicleTypes.value

        // Leave vehicleTypesLoaded false so the next caller can retry.
        vehicleTypesError.value = true

        return vehicleTypes.value
      })
      .finally(() => {
        if (thisRequest !== vehicleTypesRequestId)
          return

        vehicleTypesLoading.value = false
        vehicleTypesPromise = undefined
      })

    return vehicleTypesPromise
  }

  function invalidateVehicleTypesCache() {
    const shouldRefetch = vehicleTypesLoaded.value || vehicleTypesError.value || vehicleTypesLoading.value

    vehicleTypesRequestId += 1
    vehicleTypesLoaded.value = false
    vehicleTypesError.value = false
    vehicleTypesPromise = undefined

    if (shouldRefetch) {
      void ensureVehicleTypes()

      return
    }

    vehicleTypesLoading.value = false
  }

  function fetchVehicleType(id: number) {
    return api.vehicleTypes.vehicleTypesRetrieve({ id })
  }

  function createVehicleType(data: VehicleType) {
    return api.vehicleTypes.vehicleTypesCreate(data).then((response) => {
      invalidateVehicleTypesCache()

      return response
    })
  }

  function updateVehicleType(id: number, data: VehicleType) {
    return api.vehicleTypes.vehicleTypesUpdate({ id }, data).then((response) => {
      invalidateVehicleTypesCache()

      return response
    })
  }

  function patchVehicleType(id: number, data: PatchedVehicleType) {
    return api.vehicleTypes.vehicleTypesPartialUpdate({ id }, data).then((response) => {
      invalidateVehicleTypesCache()

      return response
    })
  }

  function deleteVehicleType(id: number) {
    return api.vehicleTypes.vehicleTypesDestroy({ id }).then((response) => {
      invalidateVehicleTypesCache()

      return response
    })
  }

  function bulkUpsertVehicleTypes(data: VehicleTypeBulk) {
    return api.vehicleTypes.vehicleTypesBulkCreate(data).then((response) => {
      invalidateVehicleTypesCache()

      return response
    })
  }

  function fetchCargoCategories(query: CargoCategoriesListParams) {
    return api.cargoCategories.cargoCategoriesList(query)
  }

  function createCargoCategory(data: CargoCategory) {
    return api.cargoCategories.cargoCategoriesCreate(data)
  }

  function updateCargoCategory(id: number, data: CargoCategory) {
    return api.cargoCategories.cargoCategoriesUpdate({ id }, data)
  }

  function patchCargoCategory(id: number, data: PatchedCargoCategory) {
    return api.cargoCategories.cargoCategoriesPartialUpdate({ id }, data)
  }

  function deleteCargoCategory(id: number) {
    return api.cargoCategories.cargoCategoriesDestroy({ id })
  }

  return {
    vehicleTypes,
    vehicleTypesLoading,
    vehicleTypesLoaded,
    vehicleTypesError,
    fetchVehicleTypes,
    ensureVehicleTypes,
    invalidateVehicleTypesCache,
    fetchVehicleType,
    createVehicleType,
    updateVehicleType,
    patchVehicleType,
    deleteVehicleType,
    bulkUpsertVehicleTypes,
    fetchCargoCategories,
    createCargoCategory,
    updateCargoCategory,
    patchCargoCategory,
    deleteCargoCategory,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useCatalogsStore, import.meta.hot))
